// Ortak bilgi baloncuğu (popover) — components/utils altında, HER YERDEN
// çağrılabilir. FAZ-53 kanal ikonu ve FAZ-54 "okundu" ikonu aynı bileşeni
// kullanır; davranış ve görünüm tek yerden yönetilir.
//
// Davranış:
//   • Masaüstü : fareyle üzerine gelince AÇILIR, ayrıca tıklama da açar/kapatır
//   • Dokunmatik: yalnız tıklama (hover olayı zaten gelmez)
//   • Kapanma   : dışarı tıklama, Escape, imleç ayrılınca (tıkla-kilitle hariç)
//
// `tiklaAc` (2026-07-29 kullanıcı isteği): hover ile AÇILMAZ, yalnız tıklama
// açar. Bilgi baloncukları (kanal ikonu, okundu ikonu) için hover doğru — imleç
// geçerken bilgi verir, bedeli yoktur. AKSİYON baloncuğu için değil: "Ne
// durumdayız?" / "Durum bildir" / "İşe Başladım" listede satır satır dizilidir,
// imleç listede gezerken kendiliğinden açılıp ekranı kapatıyordu. Bu yüzden
// davranış global DEĞİL, çağıran tarafın seçtiği bir bayrak.
// KRİTİK: tetikleyici satır tıklamasını YUTAR — kullanıcı ikona basınca
// başvuru detayına gitmemeli.
//
// NEDEN PORTAL: baloncuk, tetikleyicinin yanında çizilirse üst kapsayıcıların
// kurallarına takılıyordu:
//   1) Liste satırındaki "Ek Bilgi" hücresinde `overflow:hidden` var → panel
//      KIRPILIYOR ve "açılmıyor" gibi görünüyordu.
//   2) Satırın :hover'daki `transform`'u yeni bir yığın bağlamı yaratıyor →
//      panel sonraki satırların/sticky başlığın altında kalıyordu. Bunu satıra
//      z-index vererek çözmek TÜM SATIRI her şeyin üstüne çıkarıyor ve düzeni
//      bozuyordu.
// Panel `document.body`'ye taşınıp `position: fixed` ile konumlandırılınca iki
// sorun da kökten kalkıyor; satır CSS'ine hiç dokunmak gerekmiyor.

import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

import "./HoverPopover.css";

const KAPANMA_GECIKMESI_MS = 180; // imleç tetikleyiciden baloncuğa geçebilsin
const BOSLUK_PX = 10; // tetikleyici ile baloncuk arası
const KENAR_PAYI_PX = 8; // ekran kenarına yapışmasın

export default function HoverPopover({
  trigger,
  content,
  label,
  disabled = false,
  tiklaAc = false,
  className = "",
}) {
  const [open, setOpen] = useState(false);
  // Tıklama ile açıldıysa imleç ayrılınca kapanmaz (kilitli).
  const [pinned, setPinned] = useState(false);
  const [konum, setKonum] = useState(null);

  const wrapRef = useRef(null);
  const panelRef = useRef(null);
  const closeTimer = useRef(null);

  const clearCloseTimer = () => {
    if (closeTimer.current) {
      clearTimeout(closeTimer.current);
      closeTimer.current = null;
    }
  };

  const close = useCallback(() => {
    clearCloseTimer();
    setOpen(false);
    setPinned(false);
  }, []);

  // Konumu tetikleyiciye göre hesapla: tercihen SOLUNDA, sığmazsa sağında,
  // ikisi de olmazsa altında. Dikeyde ikonla ortalı, ekran dışına taşmaz.
  const konumHesapla = useCallback(() => {
    const trg = wrapRef.current;
    const panel = panelRef.current;
    if (!trg) return;

    const t = trg.getBoundingClientRect();
    const pw = panel?.offsetWidth || 240;
    const ph = panel?.offsetHeight || 120;
    const vw = window.innerWidth;
    const vh = window.innerHeight;

    let left;
    let yon;
    if (t.left - BOSLUK_PX - pw >= KENAR_PAYI_PX) {
      left = t.left - BOSLUK_PX - pw; // sol
      yon = "sol";
    } else if (t.right + BOSLUK_PX + pw <= vw - KENAR_PAYI_PX) {
      left = t.right + BOSLUK_PX; // sağ
      yon = "sag";
    } else {
      left = Math.min(Math.max(KENAR_PAYI_PX, t.left), vw - pw - KENAR_PAYI_PX);
      yon = "alt";
    }

    let top =
      yon === "alt"
        ? t.bottom + BOSLUK_PX
        : t.top + t.height / 2 - ph / 2;
    top = Math.min(Math.max(KENAR_PAYI_PX, top), Math.max(KENAR_PAYI_PX, vh - ph - KENAR_PAYI_PX));

    setKonum({ top, left, yon, okTop: t.top + t.height / 2 - top });
  }, []);

  useLayoutEffect(() => {
    if (!open) return undefined;
    konumHesapla();

    const yenidenHesapla = () => konumHesapla();
    window.addEventListener("resize", yenidenHesapla);
    window.addEventListener("scroll", yenidenHesapla, true);
    return () => {
      window.removeEventListener("resize", yenidenHesapla);
      window.removeEventListener("scroll", yenidenHesapla, true);
    };
  }, [open, konumHesapla]);

  // Dışarı tıklama + Escape
  useEffect(() => {
    if (!open) return undefined;

    const onDocMouseDown = (e) => {
      const icerideMi =
        (wrapRef.current && wrapRef.current.contains(e.target)) ||
        (panelRef.current && panelRef.current.contains(e.target));
      if (!icerideMi) close();
    };
    const onKeyDown = (e) => {
      if (e.key === "Escape") close();
    };

    document.addEventListener("mousedown", onDocMouseDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("mousedown", onDocMouseDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [open, close]);

  useEffect(() => clearCloseTimer, []);

  if (disabled) {
    // İzinsiz kullanıcı: ikon görünür ama tıklanabilir izlenimi VERMEZ.
    return (
      <span className={`hover-popover hover-popover--static ${className}`} title={label}>
        {trigger}
      </span>
    );
  }

  const handleEnter = () => {
    if (tiklaAc) return;
    clearCloseTimer();
    setOpen(true);
  };

  const handleLeave = () => {
    // tiklaAc: imleç ayrılınca kapanmaz — tıklayarak açılan baloncuk zaten
    // "kilitli" sayılır, dışarı tıklama ya da Escape kapatır.
    if (pinned || tiklaAc) return;
    clearCloseTimer();
    closeTimer.current = setTimeout(() => setOpen(false), KAPANMA_GECIKMESI_MS);
  };

  const handleClick = (e) => {
    // Satır tıklaması (detaya git) tetiklenmesin.
    e.stopPropagation();
    e.preventDefault();
    if (open && pinned) {
      close();
      return;
    }
    setOpen(true);
    setPinned(true);
  };

  const panel = open
    ? createPortal(
        <span
          ref={panelRef}
          className={`hover-popover-panel hover-popover-panel--bubble hover-popover-panel--${
            konum?.yon || "sol"
          }`}
          role="dialog"
          aria-label={label}
          style={{
            position: "fixed",
            top: konum ? `${konum.top}px` : "-9999px",
            left: konum ? `${konum.left}px` : "-9999px",
            visibility: konum ? "visible" : "hidden",
          }}
          onMouseEnter={clearCloseTimer}
          onMouseLeave={handleLeave}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Konuşma balonu kuyruğu — baloncuğun ikondan çıktığını gösterir. */}
          <span
            className="hover-popover-tail"
            aria-hidden="true"
            style={konum && konum.yon !== "alt" ? { top: `${konum.okTop}px` } : undefined}
          />
          {/* content FONKSİYON da olabilir: (close) => JSX. Aksiyon baloncukları
              (FAZ-56 görev çipleri) seçim yapıldıktan sonra kendini kapatabilsin
              diye eklendi; düz JSX veren mevcut çağrılar etkilenmez. */}
          {typeof content === "function" ? content(close) : content}
        </span>,
        document.body
      )
    : null;

  return (
    <span
      ref={wrapRef}
      className={`hover-popover ${open ? "hover-popover--open" : ""} ${className}`}
      onMouseEnter={handleEnter}
      onMouseLeave={handleLeave}
    >
      <button
        type="button"
        className="hover-popover-trigger"
        aria-label={label}
        aria-haspopup="dialog"
        aria-expanded={open}
        onClick={handleClick}
        onKeyDown={(e) => {
          if (e.key === "Escape") close();
        }}
      >
        {trigger}
      </button>
      {panel}
    </span>
  );
}
